import dayjs from 'dayjs';
import { DayPicker, rangeIncludesDate } from 'react-day-picker';

import { formatDate } from '../../utils/DateFormatters';
import { PRESET_NUM_OF_DAYS, COMPARISON_RANGES } from '../lib';
import { useAnalyticsStoreContext } from '../contexts';

export default function DateRangeComparatorInput({ ...props }) {
  const dateRange = useAnalyticsStoreContext((state) => state.data.first.dateRange);
  const dateRangeTwo = useAnalyticsStoreContext((state) => state.data.second.dateRange);
  const monthTwo = useAnalyticsStoreContext((state) => state.data.second.month);
  const isComparing = useAnalyticsStoreContext((state) => state.isComparing);
  const isPresetMode = useAnalyticsStoreContext((state) => state.isPresetMode);
  const selectedNumOfDays = useAnalyticsStoreContext((state) => state.selectedNumOfDays);
  const comparisonRangeDays = useAnalyticsStoreContext((state) => state.comparisonRangeDays);
  const comparisonRangeChosen = useAnalyticsStoreContext((state) => state.comparisonRangeChosen);
  const isCustomOffset = useAnalyticsStoreContext((state) => state.isCustomOffset);

  const update = useAnalyticsStoreContext((state) => state.update);
  const updateData = useAnalyticsStoreContext((state) => state.updateData);

  return (
    <div className="dropdown">
      <button
        className="btn btn-outline-primary d-flex align-items-center d-flex gap-2"
        type="button"
        data-bs-toggle="dropdown"
        data-bs-auto-close="outside"
        aria-expanded="false"
      >
        {isComparing && comparisonRangeChosen && (
          <span className="badge h-100 text-bg-secondary" style={{ top: 0 }}>
            {comparisonRangeChosen.label}
          </span>
        )}
        <i className="fe fe-calendar" />
        {dateRange?.from && dateRange?.to ? `${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}` : 'Select date range'}
        {dateRangeTwo?.from && dateRangeTwo?.to ? ` | Prior: ${formatDate(dateRangeTwo.from)} - ${formatDate(dateRangeTwo.to)}` : ''}
      </button>
      <ul className="dropdown-menu dropdown-menu-end p-0">
        <div className="d-flex flex-column">
          <div className="d-flex">
            <div className="h-100 d-flex flex-column" style={{ width: 350 }}>
              <div className="d-flex flex-column gap-2 p-3 border-bottom">
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="isEnablePresetMode"
                    checked={isPresetMode}
                    disabled={isComparing}
                    onChange={(e) => update({ isPresetMode: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="isEnablePresetMode">
                    Preset mode
                  </label>
                </div>
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="isEnableInsightsComparison"
                    checked={isComparing}
                    onChange={(e) => {
                      // Create a new range for the first date range to have the selectedNumOfDays number of days before the selected date
                      const from = dayjs(dateRange.to).subtract(selectedNumOfDays.days, 'days').toDate();
                      const to = dateRange.to;

                      update({
                        isComparing: e.target.checked,
                        isPresetMode: e.target.checked,
                        comparisonRangeChosen: null,
                        comparisonRangeDays: 0,
                        isCustomOffset: false,
                        'data.first.dateRange': { from, to },
                        'data.second.dateRange': { from: '', to: '' },
                      });
                    }}
                  />
                  <label className="form-check-label" htmlFor="isEnableInsightsComparison">
                    Compare two date ranges
                  </label>
                </div>
              </div>
              <div className="d-flex flex-column align-items-center justify-content-center gap-3 p-3">
                {isPresetMode ? (
                  <>
                    <h3>Select a preset number of days</h3>
                    <div className="d-flex flex-wrap gap-2">
                      {PRESET_NUM_OF_DAYS.map((preset) => (
                        <div key={preset.id}>
                          <input
                            type="radio"
                            className="btn-check"
                            name="presetNumOfDaysOptions"
                            id={preset.id}
                            autoComplete="off"
                            onChange={() => {
                              const from = dayjs(dateRange.to).subtract(preset.days, 'days').toDate();

                              const payload = {
                                selectedNumOfDays: preset,
                                'data.first.dateRange': { from, to: dateRange.to },
                              };

                              if (dateRangeTwo.from && dateRangeTwo.to) {
                                payload['data.second.dateRange'] = {
                                  from: dayjs(dateRangeTwo.to).subtract(preset.days, 'days').toDate(),
                                  to: dateRangeTwo.to,
                                };
                                payload['data.second.month'] = dayjs(dateRangeTwo.to);
                              }

                              update(payload);
                            }}
                            checked={preset.id === selectedNumOfDays.id}
                          />
                          <label className="btn w-100" htmlFor={preset.id}>
                            {preset.label}
                          </label>
                        </div>
                      ))}
                    </div>
                  </>
                ) : (
                  <>
                    <p className="text-muted">
                      <b>Preset mode</b> - Enable this mode to select a fixed number of days.
                    </p>
                    <p className="text-muted">
                      <b>Compare two date ranges</b> - Enable this mode to select two date ranges to compare in the dashboard. <br /> <br /> Preset mode will be
                      enabled here too as comparing two date ranges required a fixed number of days for both date ranges.
                    </p>
                  </>
                )}
              </div>
            </div>
            {isPresetMode ? (
              <DayPicker
                captionLayout="dropdown"
                numberOfMonths={2}
                timeZone="UTC"
                disabled={{ after: new Date() }}
                modifiers={{
                  selected: dateRange,
                  range_start: new Date(dateRange.from),
                  range_end: new Date(dateRange.to),
                  range_middle: (date) => (dateRange ? rangeIncludesDate(dateRange, date, { excludeEnds: true }) : false),
                }}
                onDayClick={(day) => {
                  // Set the range to be the selectedNumOfDays number of days before the selected date
                  const from = dayjs(day).subtract(selectedNumOfDays.days, 'days').toDate();
                  const to = day;

                  updateData({ first: { dateRange: { from, to } }, second: { dateRange: { from: '', to: '' }, month: '' } });
                }}
                footer={
                  dateRange?.from && dateRange?.to
                    ? `Selected first date range: ${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`
                    : 'Select a date range.'
                }
                ISOWeek
                {...props}
              />
            ) : (
              <DayPicker
                mode="range"
                captionLayout="dropdown"
                required
                numberOfMonths={2}
                timeZone="UTC"
                disabled={{ after: new Date() }}
                selected={dateRange}
                onSelect={(range) => updateData({ first: { dateRange: range } })}
                footer={
                  dateRange?.from && dateRange?.to
                    ? `Selected first date range: ${formatDate(dateRange.from)} - ${formatDate(dateRange.to)}`
                    : 'Select a date range.'
                }
                ISOWeek
                {...props}
              />
            )}
          </div>
          {isComparing && (
            <div className="d-flex border-top">
              <div className="h-100 p-3 d-flex flex-column align-items-center gap-3" style={{ width: 350 }}>
                {isCustomOffset ? (
                  <div className="d-flex flex-column align-items-center gap-3" style={{ height: 330 }}>
                    <div className="input-group mb-3">
                      <span className="input-group-text" id="comparison-range-addon-1">
                        Offset
                      </span>
                      <input
                        type="text"
                        className="form-control"
                        placeholder="e.g. 7"
                        aria-describedby="comparison-range-addon-2"
                        onChange={(e) => update({ comparisonRangeDays: e.target.value })}
                      />
                      <span className="input-group-text" id="comparison-range-addon-2">
                        Days
                      </span>
                    </div>
                    <button
                      className="btn btn-primary"
                      onClick={() => {
                        const dateRangeTwo = {
                          from: dayjs(dateRange.from).subtract(comparisonRangeDays, 'days').toISOString(),
                          to: dayjs(dateRange.to).subtract(comparisonRangeDays, 'days').toISOString(),
                        };

                        const month = dayjs(dateRangeTwo.to);

                        updateData({ second: { dateRange: dateRangeTwo, month } });
                      }}
                    >
                      Set
                    </button>
                  </div>
                ) : (
                  <div style={{ height: 330, overflowY: 'auto' }}>
                    {COMPARISON_RANGES.map((comparisonRange) => (
                      <button
                        key={comparisonRange.id}
                        className="btn"
                        onClick={() => {
                          const dateRangeTwo = comparisonRange.callback(dateRange);
                          const month = dayjs(dateRangeTwo.to);
                          update({ comparisonRangeChosen: comparisonRange, 'data.second.dateRange': dateRangeTwo, 'data.second.month': month });
                        }}
                      >
                        {comparisonRange.label}
                      </button>
                    ))}
                  </div>
                )}
                <div className="form-check form-switch">
                  <input
                    className="form-check-input"
                    type="checkbox"
                    role="switch"
                    id="isEnableCustomOffset"
                    checked={isCustomOffset}
                    onChange={(e) => update({ isCustomOffset: e.target.checked })}
                  />
                  <label className="form-check-label" htmlFor="isEnableCustomOffset">
                    Custom
                  </label>
                </div>
              </div>
              <DayPicker
                captionLayout="dropdown"
                numberOfMonths={2}
                timeZone="UTC"
                disabled={{ after: new Date(dateRange.to) }}
                month={monthTwo}
                onMonthChange={(month) => updateData({ 'second.month': month })}
                modifiers={{
                  selected: dateRangeTwo,
                  range_start: new Date(dateRangeTwo.from),
                  range_end: new Date(dateRangeTwo.to),
                  range_middle: (date) => (dateRangeTwo ? rangeIncludesDate(dateRangeTwo, date, { excludeEnds: true }) : false),
                }}
                onDayClick={(day) => {
                  // Set the selected range based on the selectedNumOfDays days
                  const from = dayjs(day).subtract(selectedNumOfDays.days, 'days').toDate();
                  const to = day;
                  const month = dayjs(to);
                  updateData({ second: { dateRange: { from, to }, month } });
                }}
                footer={
                  dateRangeTwo?.from && dateRangeTwo?.to
                    ? `Selected second date range: ${formatDate(dateRangeTwo.from)} - ${formatDate(dateRangeTwo.to)}`
                    : 'Select a second date range to compare.'
                }
                ISOWeek
                {...props}
              />
            </div>
          )}
        </div>
      </ul>
    </div>
  );
}
